import { useEffect, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getCompanyThemeAsync } from '@application/features/companyTheme/companyThemeThunk';
import { setThemeApplied, clearTheme } from '@application/features/companyTheme/companyThemeSlice';
import { APIStatus } from '@application/enums/api-status';
/**
 * Hook to automatically fetch and apply company theme based on currently selected company
 */
export const useCompanyTheme = () => {
    const dispatch = useAppDispatch();
    const { currentUser } = useAppSelector((state) => state.auth);
    const { selectedCompanyId, companies } = useAppSelector((state) => state.company);
    const { currentTheme, isThemeApplied, status } = useAppSelector((state) => state.companyTheme);
    const previousCompanyIdRef = useRef(null);
    const loadedCompanyIdRef = useRef(null);
    // When multiple companies are selected, use VDK Groep theme
    const isMultipleCompaniesSelected = selectedCompanyId.length > 1;
    const vdkGroepCompany = useMemo(() => companies.find((c) => c.name?.toLowerCase().includes('vdk groep') ||
        c.name?.toLowerCase().includes('vdkgroep') ||
        c.name?.toLowerCase() === 'vdk groep'), [companies]);
    // Determine which company ID to use for theming
    // If multiple companies selected, use VDK Groep; otherwise use first selected or primary
    const activeCompanyId = useMemo(() => {
        if (isMultipleCompaniesSelected && vdkGroepCompany) {
            return vdkGroepCompany.id;
        }
        if (selectedCompanyId.length > 0) {
            return selectedCompanyId[0];
        }
        return currentUser?.primaryCompanyId || null;
    }, [isMultipleCompaniesSelected, vdkGroepCompany, selectedCompanyId, currentUser?.primaryCompanyId]);
    useEffect(() => {
        // Skip if no active company
        if (!activeCompanyId) {
            return;
        }
        // If company changed from previous selection (compare by ID string, not array reference)
        const companyChanged = previousCompanyIdRef.current !== activeCompanyId;
        if (companyChanged) {
            // Clear the current theme first
            dispatch(clearTheme());
            dispatch(setThemeApplied(false));
            loadedCompanyIdRef.current = null;
        }
        // Update the reference
        previousCompanyIdRef.current = activeCompanyId;
        // Only fetch if:
        // 1. Theme for this company hasn't been loaded yet
        // 2. Not currently loading
        const shouldFetch = loadedCompanyIdRef.current !== activeCompanyId &&
            status !== APIStatus.PENDING;
        if (shouldFetch) {
            loadedCompanyIdRef.current = activeCompanyId;
            dispatch(getCompanyThemeAsync(activeCompanyId))
                .unwrap()
                .then(() => {
                // Mark theme as applied after successful fetch
                dispatch(setThemeApplied(true));
            })
                .catch((error) => {
                console.warn('Failed to load company theme:', error);
                // If theme fetch fails, reset loaded company and still set as applied
                loadedCompanyIdRef.current = null;
                dispatch(setThemeApplied(true));
            });
        }
    }, [activeCompanyId, status, dispatch]);
    return {
        theme: currentTheme,
        isLoading: status === APIStatus.PENDING,
        isApplied: isThemeApplied
    };
};
